import { Layout, Menu } from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  FlagOutlined,
  LogoutOutlined,
  UsergroupAddOutlined,
  AreaChartOutlined,
  StopOutlined,
  FormatPainterOutlined,
  BookOutlined,
  CreditCardOutlined,
  LinkOutlined,
  UserOutlined,
} from '@ant-design/icons';
import React, { useContext, useState, useEffect } from 'react';
import './AdminRoute.scss';
import { Outlet, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AdminContext } from '../../contexts/adminContext/adminContext';
import HeaderServerTimer from './components/HeaderServerTimer';
import AdminHeaderLocaleSwitch from './components/AdminHeaderLocaleSwitch';
import useTranslation from '../../hooks/translation/useTranslation';
import infoContentList from '../../../utils/constants/infoContentList';

const { Header, Sider, Content } = Layout;

function AdminRoute() {
  const { trans, configInfo } = useTranslation();
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const {
    setUser, resetUser, user,
  } = useContext(AdminContext);
  const isGeorgian = configInfo?.countryCode === 'GE';

  axios.interceptors.request.use((req) => {
    if (localStorage.getItem('adminAccessToken')) {
      req.headers.authorization = `Bearer ${JSON.parse(
        localStorage.getItem('adminAccessToken'),
      )}`;
    }
    return req;
  });

  const getMe = () => {
    if (localStorage.getItem('adminAccessToken')) {
      axios.get('/api/users/me').then((res) => {
        if (res?.data?.status === 'success') {
          setUser(res?.data?.user, res?.data?.token);
        }
      });
    } else {
      resetUser();
      navigate('/admin/login');
    }
  };

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      // Any status codes that falls outside the range of 2xx cause this function to trigger
      // Do something with response error
      if (error.response?.status === 403) {
        resetUser();
        navigate('/admin/login');
      }
      throw error;
    },
  );

  useEffect(() => {
    getMe();
  }, []);

  const infoItems = [{
    icon: <BookOutlined />,
    label: trans('information_corner'),
    children: (isGeorgian ? infoContentList.ge : infoContentList.am).adminItems.map((item) => ({
      ...item,
      label: trans(item.label),
    })),
  }];

  const items = [
    {
      key: '1',
      icon: <FlagOutlined />,
      label: trans('countries'),
      onClick: () => {
        navigate('/admin/countries');
      },
      roles: ['admin'],
    },
    {
      key: '2',
      icon: <UsergroupAddOutlined />,
      label: trans('users'),
      roles: ['admin'],
      children: [
        {
          key: '2-0',
          label: trans('customers'),
          onClick: () => {
            navigate('/admin/customers');
          },
        },
        {
          key: '2-1',
          label: trans('physical_customers'),
          onClick: () => {
            navigate('/admin/customers/physical');
          },
        },
        {
          key: '2-2',
          label: trans('legal_customers'),
          onClick: () => {
            navigate('/admin/customers/legal');
          },
        },
      ],
    },
    {
      key: '3',
      icon: <AreaChartOutlined />,
      label: trans('organization_types'),
      onClick: () => {
        navigate('/admin/organization-types');
      },
      roles: ['admin'],
    },
    {
      key: '4',
      icon: <StopOutlined />,
      label: trans('stations'),
      onClick: () => {
        navigate('/admin/stations');
      },
      roles: ['admin'],
    },
    {
      key: 'suggested-stations',
      icon: <StopOutlined />,
      label: trans('suggested_stations'),
      onClick: () => {
        navigate('/admin/suggested-stations');
      },
      roles: ['admin'],
    },
    {
      key: 'ride-promotions',
      icon: <StopOutlined />,
      label: trans('ride_promotions'),
      onClick: () => {
        navigate('/admin/ride-promotions');
      },
      roles: ['admin'],
    },
    {
      key: '5',
      icon: <FormatPainterOutlined />,
      label: trans('banners'),
      onClick: () => {
        navigate('/admin/banners');
      },
      roles: ['admin', 'marketing'],
    },
    {
      key: '6',
      icon: <BookOutlined />,
      label: trans('tickets'),
      onClick: () => {
        navigate('/admin/tickets');
      },
      roles: ['admin'],
    },
    ...(infoItems.map((item, index) => ({
      key: 7 + index,
      icon: item.icon,
      label: item.label,
      roles: ['admin'],
      children: item.children.map((child, childIndex) => ({
        key: `${7 + index}-${childIndex}`,
        label: child.label,
        onClick: () => {
          if (child?.noTrans) {
            navigate('/admin/info-content/notifications');
          }
        },
        children: !child?.noTrans && (isGeorgian ? [
          {
            key: `${7 + index}-${childIndex}-1`,
            label: trans('georgian'),
            onClick: () => {
              navigate(`/admin/info-content/${child.key}`);
            },
          },
          {
            key: `${7 + index}-${childIndex}-2`,
            label: `${trans('english')}`,
            onClick: () => {
              navigate(`/admin/info-content/${child.key}-en`);
            },
          },
        ] : [
          {
            key: `${7 + index}-${childIndex}-1`,
            label: trans('armenian'),
            onClick: () => {
              navigate(`/admin/info-content/${child.key}-am`);
            },
          },
          {
            key: `${7 + index}-${childIndex}-2`,
            label: `${trans('english')}`,
            onClick: () => {
              navigate(`/admin/info-content/${child.key}-en`);
            },
          },
          {
            key: `${7 + index}-${childIndex}-3`,
            label: `${trans('russian')}`,
            onClick: () => {
              navigate(`/admin/info-content/${child.key}-ru`);
            },
          },
        ]),
      })),
    }))),
    {
      key: '8',
      icon: <BookOutlined />,
      label: trans('benefits'),
      onClick: () => {
        navigate('/admin/benefits');
      },
      roles: ['admin', 'marketing'],
    },
    {
      key: '9',
      icon: <CreditCardOutlined />,
      label: trans('payments'),
      onClick: () => {
        navigate('/admin/payments');
      },
      roles: ['admin'],
    },
    {
      key: '10',
      icon: <LinkOutlined />,
      label: trans('social_links'),
      onClick: () => {
        navigate('/admin/social-links');
      },
      roles: ['admin', 'marketing'],
    },
    {
      key: '11',
      icon: <UserOutlined />,
      label: trans('admin_panel_users'),
      onClick: () => {
        navigate('/admin/users');
      },
      roles: ['admin'],
    },
    {
      key: '12',
      icon: <UserOutlined />,
      label: trans('renew'),
      onClick: () => {
        navigate('/admin/renew');
      },
      roles: ['admin'],
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: trans('logout'),
      onClick: () => {
        resetUser();
        navigate('/admin/login');
      },
      roles: ['admin', 'marketing'],
    },
  ];

  return (
    <Layout>
      <Sider trigger={null} collapsible collapsed={collapsed}>
        <div className="logo" />
        <Menu
          theme="dark"
          mode="inline"
          items={
          items?.filter(
            (item) => item.roles.find(
              (role) => role?.toLowerCase() === user?.role?.toLowerCase(),
            ),
          )
        }
        />
      </Sider>
      <Layout className="site-layout">
        <Header
          className="site-layout-background"
          style={{ padding: 0 }}
        >
          {React.createElement(collapsed ? MenuUnfoldOutlined : MenuFoldOutlined, { className: 'trigger', onClick: () => setCollapsed(!collapsed) })}

          <div className="absolute top-0 right-[50px] gap-[15px] text-[20px] flex items-center">
            <AdminHeaderLocaleSwitch />
            <HeaderServerTimer />
            <div>
              {trans('hello')}
              {' '}
              {user?.username}
            </div>
          </div>
        </Header>
        <Content
          className="site-layout-background"
          style={{ margin: '24px 16px', padding: 24, minHeight: 280 }}
        >
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}

export default AdminRoute;
